<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon kind="self" size="title" />
                <h2 class="title-subsection text-ink">Self Reflection</h2>
                <TabInfoToggle v-if="hasAnyItem" :expanded="infoOpen" topic="self reflection" @toggle="infoOpen = !infoOpen" />
                <BaseButton
                    v-if="hasAnyItem && canStart"
                    theme="primary"
                    size="sm"
                    class="ml-auto inline-flex items-center gap-2 shrink-0 whitespace-nowrap"
                    @click.prevent="emit('start-blank')">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    Start reflection
                </BaseButton>
            </div>
            <p class="body-small text-ink-muted">Reflect on how your half has gone — your reflection grounds your review and the coaching you get from Nadia.</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <HowSelfReflectionWorks v-if="!hasAnyItem" :hide-start="!canStart" @start="emit('start-blank')" />

        <BaseDialog v-model:open="infoOpen" :title="t('reflections.self.howItWorksTitle')" title-style="serif">
            <HowSelfReflectionWorks in-dialog />
            <template v-if="canStart" #footer
                ><BaseButton theme="primary" @click="onStartFromInfo">{{ t("reflections.self.launch") }}</BaseButton></template
            >
        </BaseDialog>

        <div v-if="pendingTasks.length" class="flex flex-col gap-2">
            <div class="scaffold-details-small text-ink-muted">Ready to start</div>
            <SelfTaskRow v-for="task in pendingTasks" :key="task.id" :task="task" :show-icon="false" @start="onStart" />
        </div>

        <div v-if="pastReflections.length" class="flex flex-col gap-2 mt-5">
            <div class="scaffold-details-small text-ink-muted">Previous Reflections</div>
            <PastCycleCard v-for="cycle in pastReflections" :key="cycle.id" :cycle="cycle" kind="self" @view="emit('view-cycle', $event)" />
        </div>

        <AssessmentDemoTools :cycle-actions="cycleDemoActions" bootstrap-kind="self" @deleted="emit('deleted', $event)" />
    </section>
</template>

<script setup lang="ts">
import type { PastCycleSummary, PendingPersonalTask } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { i18n } from "~vue/i18n";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import HowSelfReflectionWorks from "~vue/pages/reflections/HowSelfReflectionWorks.vue";
import PastCycleCard from "~vue/pages/reflections/PastCycleCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SelfTaskRow from "~vue/pages/reflections/SelfTaskRow.vue";
import TabInfoToggle from "~vue/pages/reflections/TabInfoToggle.vue";
import { computed, ref } from "vue";

const { t } = i18n.global;

interface Props {
    pendingTasks: PendingPersonalTask[];
    pastReflections: PastCycleSummary[];
    // False while an active org campaign owns every self-reflection template.
    canStart?: boolean;
}

const props = withDefaults(defineProps<Props>(), { canStart: false });
const emit = defineEmits<{
    (event: "start", payload: { cycleId: string; title: string; closesAt: string | null }): void;
    (event: "view-cycle", cycleId: string): void;
    (event: "start-from-campaign", payload: { campaignId: string; title: string; isSelfAssessmentOnly: boolean }): void;
    (event: "start-blank"): void;
    (event: "deleted", cycleId: string): void;
}>();

function onStart(task: PendingPersonalTask) {
    if (task.kind === "self_reflection_cycle") {
        emit("start", { cycleId: task.id, title: task.title, closesAt: task.closes_at ?? null });
        return;
    }
    if (task.kind === "self_reflection_campaign") {
        emit("start-from-campaign", { campaignId: task.id, title: task.title, isSelfAssessmentOnly: true });
    }
}

const hasAnyItem = computed(() => props.pendingTasks.length > 0 || props.pastReflections.length > 0);
// Campaign-kind tasks carry a campaign id rather than a cycle id, so they have
// no cycle to fill or delete yet.
const cycleDemoActions = computed(() => [
    ...props.pendingTasks.filter((task) => task.kind === "self_reflection_cycle").map((task) => ({ id: task.id, label: task.title })),
    ...props.pastReflections.map((cycle) => ({ id: cycle.id, label: cycle.template.name, canFill: false })),
]);
const infoOpen = ref(false);

function onStartFromInfo() {
    infoOpen.value = false;
    emit("start-blank");
}
</script>
